import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { db } from "../firebase";
import { ref, get } from "firebase/database";

export default function Login() {
  const [userId, setUserId] = useState("");
  const [password, setPassword] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [rememberMe, setRememberMe] = useState(false);
  const [schoolLogo, setSchoolLogo] = useState(null);
  const [schoolName, setSchoolName] = useState("MIS Library System");
  const navigate = useNavigate();

  // Load school logo from public folder
  useEffect(() => {
    const logoPaths = [
      '/school-logo.jpeg',
      '/school-logo.jpg',
      '/logo.png',
      '/logo.jpg',
      '/school.png'
    ];
    
    for (const path of logoPaths) {
      const img = new Image();
      img.onload = () => {
        setSchoolLogo(path);
      };
      img.src = path;
    }
  }, []);

  // Check if user has saved credentials
  useEffect(() => {
    const savedUserId = localStorage.getItem("savedUserId");
    const savedPassword = localStorage.getItem("savedPassword");
    if (savedUserId && savedPassword) {
      setUserId(savedUserId);
      setPassword(savedPassword);
      setRememberMe(true);
    }
  }, []);

  // LOGIN FUNCTION - Authenticates both Admin and Teachers
  const handleLogin = async (e) => {
    e.preventDefault();
    
    if (!userId.trim() || !password.trim()) {
      alert("Please enter both User ID and Password");
      return;
    }
    
    setIsLoading(true);
    
    try {
      // First check for Admin from Firebase
      const adminRef = ref(db, "admin");
      const adminSnapshot = await get(adminRef);
      const adminData = adminSnapshot.val();
      const validAdminPassword = adminData?.password || "admin9519";
      
      if (userId === "misinternational" && password === validAdminPassword) {
        // Admin login successful
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("role", "admin");
        localStorage.setItem("userId", userId);
        localStorage.setItem("userName", "Admin User");
        
        if (rememberMe) {
          localStorage.setItem("savedUserId", userId);
          localStorage.setItem("savedPassword", password);
        } else {
          localStorage.removeItem("savedUserId");
          localStorage.removeItem("savedPassword");
        }
        
        navigate("/admin-dashboard");
        setIsLoading(false);
        return;
      }
      
      // Check for Teacher in Firebase
      const usersRef = ref(db, "users");
      const snapshot = await get(usersRef);
      const users = snapshot.val();
      
      let foundTeacher = null;
      let teacherId = null;
      
      // Search for teacher by userId (teacherId or mobile or name)
      if (users) {
        for (const [id, user] of Object.entries(users)) {
          if (user.role === "teacher") {
            // Check by teacherId
            if (user.teacherId === userId) {
              foundTeacher = user;
              teacherId = id;
              break;
            }
            // Check by mobile number
            else if (user.mobile === userId) {
              foundTeacher = user;
              teacherId = id;
              break;
            }
            // Check by name (case insensitive)
            else if (user.name && user.name.toLowerCase() === userId.toLowerCase()) {
              foundTeacher = user;
              teacherId = id;
              break;
            }
          }
        }
      }
      
      // Validate teacher credentials
      if (foundTeacher && foundTeacher.password === password) {
        // Teacher login successful
        localStorage.setItem("isAuthenticated", "true");
        localStorage.setItem("role", "teacher");
        localStorage.setItem("userId", teacherId);
        localStorage.setItem("userName", foundTeacher.name);
        localStorage.setItem("teacherId", foundTeacher.teacherId);
        localStorage.setItem("teacherMobile", foundTeacher.mobile || "");
        
        if (rememberMe) {
          localStorage.setItem("savedUserId", userId);
          localStorage.setItem("savedPassword", password);
        } else {
          localStorage.removeItem("savedUserId");
          localStorage.removeItem("savedPassword");
        }
        
        navigate("/teacher-dashboard");
      } else if (foundTeacher && foundTeacher.password !== password) {
        alert("Invalid Password! Please try again.");
      } else {
        alert("Invalid User ID or Password! Please check your credentials.");
      }
      
    } catch (error) {
      console.error("Login error:", error);
      alert("Login failed. Please check your internet connection and try again.");
    } finally {
      setIsLoading(false);
    }
  };

  // Handle Enter key press
  const handleKeyPress = (e) => {
    if (e.key === "Enter") {
      handleLogin(e);
    }
  };

  // Get current year for copyright
  const currentYear = new Date().getFullYear();

  return (
    <div style={styles.container}>
      {/* BACKGROUND DECORATION */}
      <div style={styles.bgDecoration}>
        <div style={styles.bgCircle1}></div>
        <div style={styles.bgCircle2}></div>
        <div style={styles.bgCircle3}></div>
        <div style={styles.bgCircle4}></div>
      </div>
      
      {/* LOGIN CARD */}
      <div style={styles.card}>
        {/* LOGO SECTION */}
        <div style={styles.logoWrapper}>
          {schoolLogo ? (
            <img 
              src={schoolLogo} 
              alt="School Logo" 
              style={styles.schoolLogo}
              onError={(e) => {
                e.target.style.display = 'none';
                setSchoolLogo(null);
              }}
            />
          ) : (
            <div style={styles.logoIcon}>📚</div>
          )}
        </div>

        <h2 style={styles.title}>{schoolName}</h2>
        <p style={styles.subtitle}>Welcome back! Please login to continue</p>

        {/* LOGIN FORM */}
        <form onSubmit={handleLogin} style={styles.form}>
          {/* USER ID INPUT */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>👤</span>
              User ID / Teacher ID / Mobile
            </label>
            <input
              type="text"
              placeholder="Enter your User ID, Teacher ID or Mobile Number"
              value={userId}
              onChange={(e) => setUserId(e.target.value)}
              onKeyPress={handleKeyPress}
              style={styles.input}
              autoComplete="username"
              disabled={isLoading}
            />
          </div>

          {/* PASSWORD INPUT */}
          <div style={styles.inputGroup}>
            <label style={styles.label}>
              <span style={styles.labelIcon}>🔒</span>
              Password
            </label>
            <div style={styles.passwordWrapper}>
              <input
                type={showPassword ? "text" : "password"}
                placeholder="Enter your password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                onKeyPress={handleKeyPress}
                style={styles.passwordInput}
                autoComplete="current-password"
                disabled={isLoading}
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                style={styles.eyeBtn}
              >
                {showPassword ? "👁️" : "👁️‍🗨️"}
              </button>
            </div>
          </div>

          {/* REMEMBER ME & FORGOT PASSWORD */}
          <div style={styles.options}>
            <label style={styles.checkboxLabel}>
              <input 
                type="checkbox" 
                checked={rememberMe}
                onChange={(e) => setRememberMe(e.target.checked)}
                style={styles.checkbox} 
              />
              Remember me
            </label>
            
          </div>

          {/* LOGIN BUTTON */}
          <button 
            type="submit" 
            style={{
              ...styles.button,
              ...(isLoading ? styles.buttonDisabled : {}),
              ...(userId && password ? styles.buttonActive : {})
            }}
            disabled={isLoading}
          >
            {isLoading ? (
              <span style={styles.loaderContainer}>
                <span style={styles.loader}></span>
                Logging in...
              </span>
            ) : (
              "Login"
            )}
          </button>
        </form>

      

        {/* DIVIDER */}
        <div style={styles.divider}>
          <span style={styles.dividerText}>Secure Access</span>
        </div>

        {/* FOOTER WITH COPYRIGHT */}
        <div style={styles.footer}>
          <p style={styles.copyright}>
            © {currentYear} Jauhari WebTech. All Rights Reserved.
          </p>
          <p style={styles.developer}>
            Developed by <strong>Jauhari WebTech</strong>
          </p>
          <p style={styles.version}>Version 2.0 | MIS Library System</p>
        </div>
      </div>
    </div>
  );
}

const styles = {
  container: {
    minHeight: "100vh",
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    fontFamily: "'Segoe UI', Tahoma, Geneva, Verdana, sans-serif",
    position: "relative",
    overflow: "hidden",
    padding: "20px",
    overflowY: "auto",  // Add this
    WebkitOverflowScrolling: "touch",  // Add this
  },

  bgDecoration: {
    position: "absolute",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    overflow: "hidden",
  },

  bgCircle1: {
    position: "absolute",
    top: "-20%",
    right: "-10%",
    width: "500px",
    height: "500px",
    background: "rgba(255, 255, 255, 0.1)",
    borderRadius: "50%",
    pointerEvents: "none",
    animation: "float 20s ease-in-out infinite",
  },

  bgCircle2: {
    position: "absolute",
    bottom: "-20%",
    left: "-10%",
    width: "400px",
    height: "400px",
    background: "rgba(255, 255, 255, 0.05)",
    borderRadius: "50%",
    pointerEvents: "none",
    animation: "float 25s ease-in-out infinite reverse",
  },

  bgCircle3: {
    position: "absolute",
    top: "30%",
    left: "30%",
    width: "300px",
    height: "300px",
    background: "rgba(255, 255, 255, 0.03)",
    borderRadius: "50%",
    pointerEvents: "none",
    animation: "float 15s ease-in-out infinite",
  },

  bgCircle4: {
    position: "absolute",
    bottom: "10%",
    right: "20%",
    width: "250px",
    height: "250px",
    background: "rgba(255, 255, 255, 0.04)",
    borderRadius: "50%",
    pointerEvents: "none",
    animation: "float 18s ease-in-out infinite reverse",
  },

  card: {
    background: "#fff",
    padding: "clamp(30px, 5vw, 50px)",
    borderRadius: "20px",
    width: "100%",
    maxWidth: "450px",
    textAlign: "center",
    boxShadow: "0 20px 60px rgba(0, 0, 0, 0.3)",
    transition: "transform 0.3s ease, box-shadow 0.3s ease",
    position: "relative",
    zIndex: 1,
    backdropFilter: "blur(10px)",
    backgroundColor: "rgba(255, 255, 255, 0.98)",
  },

  logoWrapper: {
    marginBottom: "20px",
  },

  schoolLogo: {
    width: "clamp(80px, 20vw, 100px)",
    height: "clamp(80px, 20vw, 100px)",
    objectFit: "contain",
    marginBottom: "10px",
    borderRadius: "50%",
    boxShadow: "0 4px 15px rgba(0, 0, 0, 0.1)",
  },

  logoIcon: {
    fontSize: "clamp(50px, 15vw, 70px)",
    marginBottom: "10px",
    display: "inline-block",
    animation: "bounce 2s ease infinite",
  },

  title: {
    margin: "10px 0 5px",
    color: "#333",
    fontSize: "clamp(20px, 5vw, 26px)",
    fontWeight: "600",
  },

  subtitle: {
    marginBottom: "30px",
    color: "#777",
    fontSize: "clamp(13px, 4vw, 14px)",
  },

  form: {
    display: "flex",
    flexDirection: "column",
    gap: "5px",
  },

  inputGroup: {
    textAlign: "left",
    marginBottom: "15px",
  },

  label: {
    display: "block",
    marginBottom: "8px",
    color: "#555",
    fontSize: "clamp(12px, 3.5vw, 14px)",
    fontWeight: "500",
  },

  labelIcon: {
    marginRight: "5px",
  },

  input: {
    width: "100%",
    padding: "clamp(12px, 3vw, 14px)",
    borderRadius: "10px",
    border: "2px solid #e0e0e0",
    outline: "none",
    fontSize: "clamp(14px, 4vw, 16px)",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    backgroundColor: "#fafafa",
  },

  passwordWrapper: {
    position: "relative",
    width: "100%",
  },

  passwordInput: {
    width: "100%",
    padding: "clamp(12px, 3vw, 14px)",
    paddingRight: "45px",
    borderRadius: "10px",
    border: "2px solid #e0e0e0",
    outline: "none",
    fontSize: "clamp(14px, 4vw, 16px)",
    transition: "all 0.3s ease",
    boxSizing: "border-box",
    backgroundColor: "#fafafa",
  },

  eyeBtn: {
    position: "absolute",
    right: "12px",
    top: "50%",
    transform: "translateY(-50%)",
    background: "none",
    border: "none",
    cursor: "pointer",
    fontSize: "18px",
    padding: "0",
  },

  options: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: "25px",
    flexWrap: "wrap",
    gap: "10px",
  },

  checkboxLabel: {
    display: "flex",
    alignItems: "center",
    gap: "8px",
    color: "#666",
    fontSize: "clamp(12px, 3.5vw, 13px)",
    cursor: "pointer",
  },

  checkbox: {
    width: "16px",
    height: "16px",
    cursor: "pointer",
  },

  forgotLink: {
    color: "#667eea",
    textDecoration: "none",
    fontSize: "clamp(12px, 3.5vw, 13px)",
    transition: "color 0.3s ease",
  },

  button: {
    width: "100%",
    padding: "clamp(12px, 3.5vw, 14px)",
    borderRadius: "10px",
    border: "none",
    background: "#ccc",
    color: "#fff",
    fontWeight: "bold",
    cursor: "pointer",
    fontSize: "clamp(14px, 4vw, 16px)",
    transition: "all 0.3s ease",
    marginTop: "10px",
  },

  buttonActive: {
    background: "linear-gradient(135deg, #667eea 0%, #764ba2 100%)",
  },

  buttonDisabled: {
    opacity: 0.7,
    cursor: "not-allowed",
  },

  loaderContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    gap: "10px",
  },

  loader: {
    width: "18px",
    height: "18px",
    border: "2px solid #fff",
    borderTop: "2px solid transparent",
    borderRadius: "50%",
    animation: "spin 0.8s linear infinite",
  },

  infoBox: {
    marginTop: "25px",
    padding: "15px",
    backgroundColor: "#f0f4ff",
    borderRadius: "12px",
    textAlign: "left",
  },

  infoTitle: {
    fontSize: "13px",
    fontWeight: "600",
    color: "#667eea",
    marginBottom: "10px",
  },

  infoContent: {
    fontSize: "12px",
    color: "#555",
  },

  infoRow: {
    marginBottom: "8px",
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },

  divider: {
    margin: "25px 0 15px",
    position: "relative",
    textAlign: "center",
  },

  dividerText: {
    background: "#fff",
    padding: "0 15px",
    color: "#999",
    fontSize: "12px",
    position: "relative",
    zIndex: 1,
  },

  footer: {
    marginTop: "20px",
    textAlign: "center",
  },

  copyright: {
    fontSize: "clamp(10px, 3vw, 11px)",
    color: "#999",
    margin: "5px 0",
  },

  developer: {
    fontSize: "clamp(10px, 3vw, 11px)",
    color: "#667eea",
    margin: "5px 0",
  },

  version: {
    fontSize: "clamp(9px, 2.5vw, 10px)",
    color: "#bbb",
    margin: "5px 0",
  },
};

// Add keyframes for animations
const styleSheet = document.createElement("style");
styleSheet.textContent = `
  @keyframes spin {
    0% { transform: rotate(0deg); }
    100% { transform: rotate(360deg); }
  }
  
  @keyframes bounce {
    0%, 100% { transform: translateY(0); }
    50% { transform: translateY(-10px); }
  }
  
  @keyframes float {
    0%, 100% { transform: translate(0, 0); }
    25% { transform: translate(10px, -10px); }
    50% { transform: translate(0, -20px); }
    75% { transform: translate(-10px, -10px); }
  }
  
  input:focus {
    border-color: #667eea !important;
    box-shadow: 0 0 0 3px rgba(102, 126, 234, 0.1);
  }
  
  button:hover:not(:disabled) {
    transform: translateY(-2px);
    box-shadow: 0 5px 20px rgba(102, 126, 234, 0.4);
  }
  
  a:hover {
    color: #764ba2 !important;
  }
  
  @media (max-width: 480px) {
    .card {
      margin: 0 10px;
    }
    
    .info-row {
      font-size: 10px;
    }
  }
`;
document.head.appendChild(styleSheet);